import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";

import styled, { createGlobalStyle } from "styled-components";

import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import {
    createEmptyMessageDoc,
    isMessageDocEmpty,
    parseMessageDoc,
    stringifyMessageDoc,
} from "components/atoms/message/utils";

const MessageInputGlobalStyle = createGlobalStyle`
    .message-mention-suggestion {
        position: fixed;
        z-index: 1000;
        min-width: 220px;
        max-width: 280px;
        padding: 6px;
        border: 1px solid #dfe6eb;
        border-radius: 8px;
        background: var(--color-White);
        box-shadow: 0 8px 22px rgba(15, 23, 42, 0.16);
    }

    .message-mention-suggestion-item,
    .message-mention-suggestion-empty {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        padding: 7px 8px;
        border: 0;
        border-radius: 6px;
        background: transparent;
        color: var(--color-SubBlack);
        font-size: 1.2rem;
        text-align: left;
    }

    .message-mention-suggestion-item {
        cursor: pointer;
    }

    .message-mention-suggestion-item:hover,
    .message-mention-suggestion-item.is-selected {
        background: #e8f5f1;
        color: var(--color-Key);
    }

    .message-mention-suggestion-avatar {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: #dff3ee;
        color: var(--color-Key);
        font-size: 1.1rem;
        font-weight: 800;
    }

    .message-mention-suggestion-label {
        min-width: 0;
        overflow: hidden;
        font-weight: 700;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .message-mention-suggestion-subtext {
        margin-left: auto;
        color: var(--color-Grey1);
        font-size: 1.1rem;
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
    border: 1px solid #dfe6eb;
    border-radius: 8px;
    background: var(--color-White);
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
    transition:
        border-color 120ms ease-in,
        box-shadow 120ms ease-in;

    &:focus-within {
        border-color: var(--color-Key);
        box-shadow: 0 0 0 3px rgba(15, 143, 111, 0.12);
    }
`;

const SlotRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-width: 0;
    gap: 8px;
    padding: 8px 10px;
    background: #f4f7f8;
`;

const HeaderSlot = styled.div`
    min-width: 0;
    padding: 8px 10px 0;
    background: transparent;
`;

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 2px;
    min-width: 0;
    padding: 6px 8px;
    border-bottom: 1px solid #eef2f4;
    background: #fbfcfd;
`;

const ToolbarDivider = styled.div`
    width: 1px;
    height: 18px;
    margin: 0 4px;
    background: #dfe6eb;
`;

const ToolButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    min-width: 28px;
    height: 28px;
    padding: 0 7px;
    border: 0;
    border-radius: 5px;
    background: ${(props) => (props.$active ? "var(--color-Base2)" : "transparent")};
    color: ${(props) => (props.$active ? "var(--color-Key)" : "var(--color-SubBlack)")};
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: var(--color-Base1);
    }

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }
`;

const StrikeGlyph = styled.span`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    font-size: 1.6rem;
    font-weight: 400;
    font-family:
        ui-sans-serif,
        system-ui,
        -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        sans-serif;
    line-height: 1;

    &::after {
        content: "";
        position: absolute;
        left: -2px;
        right: -2px;
        top: 50%;
        height: 1px;
        border-radius: 999px;
        background: currentColor;
        transform: translateY(-50%);
    }
`;

const EmojiGlyph = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    font-size: 1.7rem;
    font-weight: 700;
    line-height: 1;
`;

const EmojiMenu = styled.div`
    position: fixed;
    left: ${(props) => props.$left}px;
    top: ${(props) => props.$top}px;
    z-index: 1200;
    display: grid;
    grid-template-columns: repeat(8, 28px);
    gap: 2px;
    padding: 6px;
    border: 1px solid #dfe6eb;
    border-radius: 6px;
    background: var(--color-White);
    box-shadow: 0 8px 22px rgba(15, 23, 42, 0.16);
`;

const EmojiMenuAnchor = styled.div`
    position: relative;
    display: inline-flex;
`;

const InputRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    min-width: 0;
    padding: 8px 10px;
`;

const InputWrap = styled.div`
    flex: 1;
    min-width: 0;
`;

const EditorWrap = styled.div`
    width: 100%;

    .message-editor {
        width: 100%;
    }

    .ProseMirror {
        min-height: ${(props) => (props.$compact ? "28px" : "34px")};
        max-height: 180px;
        padding: 4px 0;
        outline: none;
        overflow: auto;
        color: var(--color-SubBlack);
        font-size: ${(props) => (props.$compact ? "1.3rem" : "1.4rem")};
        line-height: 1.45;
        word-break: break-word;
    }

    .ProseMirror p {
        margin: 0;
    }

    .ProseMirror ul,
    .ProseMirror ol {
        margin: 0.25em 0 0.25em 1.4em;
        padding: 0;
    }

    .ProseMirror li {
        padding-left: 0.15em;
    }

    .ProseMirror p + p {
        margin-top: 0.4em;
    }

    .ProseMirror blockquote {
        margin: 0.35em 0;
        padding: 0.25em 0 0.25em 0.8em;
        border-left: 3px solid var(--color-Key);
        background: #f4faf8;
        color: #40534f;
    }

    .ProseMirror code {
        padding: 1px 5px;
        border-radius: 4px;
        background: #f4f7f8;
        color: #d94b7f;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
        font-size: 0.92em;
    }

    .ProseMirror a {
        color: #116dff;
        text-decoration: underline;
        text-underline-offset: 2px;
    }

    .ProseMirror p.is-editor-empty:first-child::before {
        content: attr(data-placeholder);
        float: left;
        height: 0;
        color: var(--color-Grey2);
        pointer-events: none;
    }

    .message-mention {
        display: inline-flex;
        align-items: center;
        min-height: 1.4em;
        padding: 0 4px;
        border-radius: 4px;
        background: #e8f5f1;
        color: var(--color-Key);
        font-weight: 700;
    }
`;

const ActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: ${(props) =>
        props.$danger ? "var(--color-Warn)" : props.$primary ? "var(--color-Button1)" : "transparent"};
    color: ${(props) => (props.$primary ? "var(--color-White)" : "var(--color-SubBlack)")};
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${(props) =>
            props.$danger
                ? "var(--color-WarnHover)"
                : props.$primary
                  ? "var(--color-ButtonHover1)"
                  : "var(--color-Base1)"};
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const TextButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 28px;
    padding: 0 12px;
    border: 1px solid ${(props) => (props.$primary ? "var(--color-Key)" : "#d9dde2")};
    border-radius: 4px;
    background: ${(props) => (props.$primary ? "var(--color-Key)" : "#ffffff")};
    color: ${(props) => (props.$primary ? "#ffffff" : "var(--color-SubBlack)")};
    font-size: 1.2rem;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${(props) => (props.$primary ? "var(--color-DarkKey)" : "#eef2f5")};
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const renderAction = (action, options = {}) => {
    if (!action) return null;
    const isDanger = !!options.danger;

    const button = action.label ? (
        <TextButton
            key={action.key || action.label}
            type="button"
            $primary={action.primary}
            onClick={action.onClick}
            disabled={action.disabled}
        >
            {action.label}
        </TextButton>
    ) : (
        <ActionButton
            key={action.key || action.icon}
            type="button"
            aria-label={action.tooltip}
            $primary={action.primary}
            $danger={isDanger}
            onClick={action.onClick}
            disabled={action.disabled}
        >
            <Icon
                name={action.icon}
                size={action.iconSize || "12"}
                color={action.primary || isDanger ? "var(--color-White)" : "currentColor"}
            />
        </ActionButton>
    );

    return action.tooltip ? (
        <Tooltip key={action.key || action.label || action.icon} message={action.tooltip}>
            {button}
        </Tooltip>
    ) : (
        button
    );
};

const createMentionSuggestion = (items = []) => ({
    char: "@",
    items: ({ query }) => items.filter((item) => item.display?.toLowerCase().includes(query.toLowerCase())).slice(0, 8),
    render: () => {
        let container = null;
        let selectedIndex = 0;
        let currentProps = null;
        let cleanupPositionListeners = null;

        const updatePosition = (props) => {
            const rect = props.clientRect?.();
            if (!container || !rect) return;
            const margin = 8;
            const containerRect = container.getBoundingClientRect();
            const width = containerRect.width || 240;
            const height = containerRect.height || 48;
            const left = Math.min(Math.max(margin, rect.left), Math.max(margin, window.innerWidth - width - margin));
            const belowTop = rect.bottom + 6;
            const aboveTop = rect.top - height - 6;
            const top = belowTop + height <= window.innerHeight - margin ? belowTop : Math.max(margin, aboveTop);
            container.style.left = `${left}px`;
            container.style.top = `${top}px`;
        };

        const renderItems = () => {
            if (!container || !currentProps) return;
            container.innerHTML = "";
            const list = currentProps.items || [];

            if (!list.length) {
                const empty = document.createElement("div");
                empty.className = "message-mention-suggestion-empty";
                empty.textContent = "멘션 없음";
                container.appendChild(empty);
                return;
            }

            list.forEach((item, index) => {
                const button = document.createElement("button");
                button.type = "button";
                button.className = `message-mention-suggestion-item${index === selectedIndex ? " is-selected" : ""}`;
                const avatar = document.createElement("span");
                avatar.className = "message-mention-suggestion-avatar";
                avatar.textContent = String(item.display || "?")
                    .slice(0, 1)
                    .toUpperCase();
                const label = document.createElement("span");
                label.className = "message-mention-suggestion-label";
                label.textContent = `@${item.display}`;
                const subtext = document.createElement("span");
                subtext.className = "message-mention-suggestion-subtext";
                subtext.textContent = String(item.id) === "0" ? "AI" : "멤버";
                button.appendChild(avatar);
                button.appendChild(label);
                button.appendChild(subtext);
                button.onclick = () => currentProps.command({ id: String(item.id), label: item.display });
                container.appendChild(button);
            });
            updatePosition(currentProps);
        };

        return {
            onStart: (props) => {
                currentProps = props;
                selectedIndex = 0;
                container = document.createElement("div");
                container.className = "message-mention-suggestion";
                document.body.appendChild(container);
                const handleViewportChange = () => updatePosition(currentProps);
                window.addEventListener("resize", handleViewportChange);
                window.addEventListener("scroll", handleViewportChange, true);
                cleanupPositionListeners = () => {
                    window.removeEventListener("resize", handleViewportChange);
                    window.removeEventListener("scroll", handleViewportChange, true);
                };
                updatePosition(props);
                renderItems();
            },
            onUpdate: (props) => {
                currentProps = props;
                selectedIndex = 0;
                updatePosition(props);
                renderItems();
            },
            onKeyDown: (props) => {
                const list = currentProps?.items || [];
                if (props.event.key === "ArrowDown") {
                    selectedIndex = list.length ? (selectedIndex + 1) % list.length : 0;
                    renderItems();
                    return true;
                }
                if (props.event.key === "ArrowUp") {
                    selectedIndex = list.length ? (selectedIndex + list.length - 1) % list.length : 0;
                    renderItems();
                    return true;
                }
                if (props.event.key === "Enter" && list[selectedIndex]) {
                    currentProps.command({ id: String(list[selectedIndex].id), label: list[selectedIndex].display });
                    return true;
                }
                return false;
            },
            onExit: () => {
                cleanupPositionListeners?.();
                cleanupPositionListeners = null;
                container?.remove();
                container = null;
                currentProps = null;
            },
        };
    },
});

const DEFAULT_FEATURES = {
    formatting: true,
    link: true,
    mention: true,
    emoji: true,
    list: true,
    quote: true,
};

const EMOJI_OPTIONS = ["😀", "😂", "😊", "😍", "👍", "👏", "🙏", "🎉", "🔥", "✅", "💡", "👀", "🤔", "😮", "😢", "🚀"];

const normalizeFeatures = (features) => {
    if (features === false) {
        return Object.fromEntries(Object.keys(DEFAULT_FEATURES).map((key) => [key, false]));
    }
    return {
        ...DEFAULT_FEATURES,
        ...(features || {}),
    };
};

const hasToolbarFeature = (features) => Object.values(features).some(Boolean);

const isEditorUsable = (editor) => Boolean(editor && !editor.isDestroyed);

function MessageInput(
    {
        value = "",
        onChange,
        onSubmit,
        onCancel,
        placeholder = "",
        disabled = false,
        autoFocus = false,
        multiline = true,
        headerSlot = null,
        footerSlot = null,
        bottomSlot = null,
        leftSlot = null,
        actions = [],
        submitTooltip = "전송",
        submitIcon = "enter",
        submitLabel = "",
        submitDisabled,
        submitLoading = false,
        submitPrimary = true,
        cancelLabel = "",
        cancelTooltip = "취소",
        cancelDisabled = false,
        showSubmit = true,
        mentions = null,
        features = undefined,
        stopAction = null,
        className,
        onClick,
        onMouseDown,
        onMouseUp,
        onBlur,
    },
    ref,
) {
    const lastValueRef = useRef(value || "");
    const emojiButtonRef = useRef(null);
    const editorRef = useRef(null);
    const submitDisabledRef = useRef(false);
    const submitValueRef = useRef(value || "");
    const onSubmitRef = useRef(onSubmit);
    const onCancelRef = useRef(onCancel);
    const multilineRef = useRef(multiline);
    const [editorStateVersion, setEditorStateVersion] = useState(0);
    const [isEmojiMenuOpen, setIsEmojiMenuOpen] = useState(false);
    const [emojiMenuPosition, setEmojiMenuPosition] = useState({ left: 0, top: 0 });
    const enabledFeatures = useMemo(() => normalizeFeatures(features), [features]);
    const mentionItems = useMemo(() => mentions?.data || [], [mentions?.data]);
    const isSubmitDisabled =
        submitDisabled !== undefined ? submitDisabled : disabled || submitLoading || isMessageDocEmpty(value);

    submitDisabledRef.current = isSubmitDisabled;
    submitValueRef.current = value;
    onSubmitRef.current = onSubmit;
    onCancelRef.current = onCancel;
    multilineRef.current = multiline;

    const handleSubmit = (event, editorOverride = null) => {
        if (isSubmitDisabled) return;
        const editorJson = isEditorUsable(editorOverride) ? editorOverride.getJSON() : null;
        onSubmit?.(editorJson ? stringifyMessageDoc(editorJson) : value, event);
    };

    const editor = useEditor(
        {
            extensions: [
                StarterKit.configure({
                    heading: false,
                    link: false,
                }),
                Link.configure({
                    openOnClick: false,
                    autolink: true,
                    linkOnPaste: true,
                    protocols: ["http", "https"],
                }),
                Placeholder.configure({
                    placeholder,
                }),
                Mention.configure({
                    HTMLAttributes: {
                        class: "message-mention",
                    },
                    renderText: ({ node }) => `@${node.attrs.label || node.attrs.id}`,
                    renderHTML: ({ node }) => [
                        "span",
                        {
                            class: "message-mention",
                            "data-type": "mention",
                            "data-id": node.attrs.id,
                            "data-label": node.attrs.label,
                        },
                        `@${node.attrs.label || node.attrs.id}`,
                    ],
                    suggestion: createMentionSuggestion(mentionItems),
                }),
            ],
            content: (() => {
                try {
                    return parseMessageDoc(value);
                } catch {
                    return createEmptyMessageDoc();
                }
            })(),
            editable: !disabled,
            autofocus: autoFocus,
            editorProps: {
                attributes: {
                    class: "message-editor",
                    placeholder,
                    "aria-placeholder": placeholder,
                },
                handleKeyDown: (view, event) => {
                    if (event.key === "Escape" && onCancelRef.current) {
                        event.preventDefault();
                        onCancelRef.current(event);
                        return true;
                    }
                    if (event.key !== "Enter" || event.isComposing) return false;
                    if (document.querySelector(".message-mention-suggestion")) return false;
                    if (multilineRef.current && event.shiftKey) {
                        const currentEditor = editorRef.current;
                        return isEditorUsable(currentEditor) ? currentEditor.commands.setHardBreak() : false;
                    }
                    event.preventDefault();
                    if (submitDisabledRef.current) return true;
                    const currentEditor = editorRef.current;
                    const nextValue = isEditorUsable(currentEditor)
                        ? stringifyMessageDoc(currentEditor.getJSON())
                        : submitValueRef.current;
                    onSubmitRef.current?.(nextValue, event);
                    return true;
                },
            },
            onUpdate: ({ editor: nextEditor }) => {
                const nextValue = stringifyMessageDoc(nextEditor.getJSON());
                lastValueRef.current = nextValue;
                onChange?.(nextValue);
                setEditorStateVersion((version) => version + 1);
            },
            onSelectionUpdate: () => setEditorStateVersion((version) => version + 1),
            onBlur,
        },
        [mentionItems, placeholder, multiline, disabled],
    );

    useEffect(() => {
        if (!editor) return;
        editorRef.current = editor;
        return () => {
            if (editorRef.current === editor) {
                editorRef.current = null;
            }
        };
    }, [editor]);

    useEffect(() => {
        if (!isEditorUsable(editor)) return;
        editor.setEditable(!disabled);
    }, [disabled, editor]);

    useEffect(() => {
        if (!isEmojiMenuOpen) return;
        const updateEmojiMenuPosition = () => {
            const rect = emojiButtonRef.current?.getBoundingClientRect();
            if (!rect) return;
            const menuWidth = 236;
            const menuHeight = 68;
            const margin = 8;
            const left = Math.min(
                Math.max(margin, rect.left),
                Math.max(margin, window.innerWidth - menuWidth - margin),
            );
            const top = rect.top - menuHeight - 8 > margin ? rect.top - menuHeight - 8 : rect.bottom + 8;
            setEmojiMenuPosition({
                left,
                top: Math.min(Math.max(margin, top), Math.max(margin, window.innerHeight - menuHeight - margin)),
            });
        };

        updateEmojiMenuPosition();
        window.addEventListener("resize", updateEmojiMenuPosition);
        window.addEventListener("scroll", updateEmojiMenuPosition, true);
        return () => {
            window.removeEventListener("resize", updateEmojiMenuPosition);
            window.removeEventListener("scroll", updateEmojiMenuPosition, true);
        };
    }, [isEmojiMenuOpen]);

    useEffect(() => {
        if (!isEditorUsable(editor)) return;
        if ((value || "") === lastValueRef.current) return;
        try {
            const nextDoc = parseMessageDoc(value);
            lastValueRef.current = stringifyMessageDoc(nextDoc);
            editor.commands.setContent(nextDoc, false);
        } catch {
            const emptyDoc = createEmptyMessageDoc();
            lastValueRef.current = stringifyMessageDoc(emptyDoc);
            editor.commands.setContent(emptyDoc, false);
        }
    }, [editor, value]);

    useImperativeHandle(ref, () => ({
        focus: () => {
            const currentEditor = editorRef.current;
            if (!isEditorUsable(currentEditor)) return;
            currentEditor.commands.focus();
        },
        blur: () => {
            const currentEditor = editorRef.current;
            if (!isEditorUsable(currentEditor)) return;
            currentEditor.commands.blur();
        },
        get element() {
            const currentEditor = editorRef.current;
            return isEditorUsable(currentEditor) ? currentEditor.view?.dom || null : null;
        },
    }));

    const submitAction = submitLabel
        ? {
              key: "submit",
              label: submitLabel,
              primary: submitPrimary,
              tooltip: submitTooltip,
              disabled: isSubmitDisabled,
              onClick: (event) => handleSubmit(event, editor),
          }
        : {
              key: "submit",
              icon: submitIcon,
              tooltip: submitTooltip,
              primary: submitPrimary,
              disabled: isSubmitDisabled,
              onClick: (event) => handleSubmit(event, editor),
          };
    const cancelAction =
        onCancel && cancelLabel
            ? {
                  key: "cancel",
                  label: cancelLabel,
                  tooltip: cancelTooltip,
                  disabled: cancelDisabled,
                  onClick: onCancel,
              }
            : null;

    const renderedActions = stopAction ? [renderAction(stopAction, { danger: true })] : [...actions.map(renderAction)];
    if (onCancel && cancelAction) {
        renderedActions.push(renderAction(cancelAction));
    }
    if (showSubmit && !stopAction) {
        renderedActions.push(renderAction(submitAction));
    }

    const runEditorCommand = (command) => {
        if (!isEditorUsable(editor) || disabled) return;
        command(editor);
        setEditorStateVersion((version) => version + 1);
    };

    const applyLink = () => {
        if (!isEditorUsable(editor) || disabled) return;
        const previousHref = editor.getAttributes("link")?.href || "";
        const href = window.prompt("링크 URL", previousHref);
        if (href === null) return;
        const nextHref = href.trim();
        if (!nextHref) {
            editor.chain().focus().unsetLink().run();
            setEditorStateVersion((version) => version + 1);
            return;
        }
        const normalizedHref = /^https?:\/\//i.test(nextHref) ? nextHref : `https://${nextHref}`;
        editor.chain().focus().extendMarkRange("link").setLink({ href: normalizedHref }).run();
        setEditorStateVersion((version) => version + 1);
    };

    const insertEmoji = (emoji) => {
        runEditorCommand((nextEditor) => nextEditor.chain().focus().insertContent(emoji).run());
        setIsEmojiMenuOpen(false);
    };

    const insertMentionTrigger = () => {
        runEditorCommand((nextEditor) => nextEditor.chain().focus().insertContent("@").run());
    };

    const toolbar = hasToolbarFeature(enabledFeatures) ? (
        <Toolbar data-editor-state-version={editorStateVersion}>
            {enabledFeatures.formatting ? (
                <>
                    <Tooltip message="굵게">
                        <ToolButton
                            type="button"
                            aria-label="굵게"
                            $active={!!editor?.isActive("bold")}
                            disabled={disabled}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() =>
                                runEditorCommand((nextEditor) => nextEditor.chain().focus().toggleBold().run())
                            }
                        >
                            <Icon name="textBold" size="13" color="currentColor" />
                        </ToolButton>
                    </Tooltip>
                    <Tooltip message="기울임">
                        <ToolButton
                            type="button"
                            aria-label="기울임"
                            $active={!!editor?.isActive("italic")}
                            disabled={disabled}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() =>
                                runEditorCommand((nextEditor) => nextEditor.chain().focus().toggleItalic().run())
                            }
                        >
                            <Icon name="textItalic" size="13" color="currentColor" />
                        </ToolButton>
                    </Tooltip>
                    <Tooltip message="취소선">
                        <ToolButton
                            type="button"
                            aria-label="취소선"
                            $active={!!editor?.isActive("strike")}
                            disabled={disabled}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() =>
                                runEditorCommand((nextEditor) => nextEditor.chain().focus().toggleStrike().run())
                            }
                        >
                            <StrikeGlyph>S</StrikeGlyph>
                        </ToolButton>
                    </Tooltip>
                    <Tooltip message="코드">
                        <ToolButton
                            type="button"
                            aria-label="코드"
                            $active={!!editor?.isActive("code")}
                            disabled={disabled}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() =>
                                runEditorCommand((nextEditor) => nextEditor.chain().focus().toggleCode().run())
                            }
                        >
                            {"</>"}
                        </ToolButton>
                    </Tooltip>
                </>
            ) : null}
            {enabledFeatures.link ? (
                <>
                    {enabledFeatures.formatting ? <ToolbarDivider /> : null}
                    <Tooltip message="링크">
                        <ToolButton
                            type="button"
                            aria-label="링크"
                            $active={!!editor?.isActive("link")}
                            disabled={disabled}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={applyLink}
                        >
                            <Icon name="link" size="13" color="currentColor" />
                        </ToolButton>
                    </Tooltip>
                </>
            ) : null}
            {enabledFeatures.list || enabledFeatures.quote ? (
                <>
                    {enabledFeatures.formatting || enabledFeatures.link ? <ToolbarDivider /> : null}
                    {enabledFeatures.list ? (
                        <>
                            <Tooltip message="글머리 목록">
                                <ToolButton
                                    type="button"
                                    aria-label="글머리 목록"
                                    $active={!!editor?.isActive("bulletList")}
                                    disabled={disabled}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() =>
                                        runEditorCommand((nextEditor) =>
                                            nextEditor.chain().focus().toggleBulletList().run(),
                                        )
                                    }
                                >
                                    <Icon name="list" size="13" color="currentColor" />
                                </ToolButton>
                            </Tooltip>
                            <Tooltip message="번호 목록">
                                <ToolButton
                                    type="button"
                                    aria-label="번호 목록"
                                    $active={!!editor?.isActive("orderedList")}
                                    disabled={disabled}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() =>
                                        runEditorCommand((nextEditor) =>
                                            nextEditor.chain().focus().toggleOrderedList().run(),
                                        )
                                    }
                                >
                                    <Icon name="listNum" size="13" color="currentColor" />
                                </ToolButton>
                            </Tooltip>
                        </>
                    ) : null}
                    {enabledFeatures.quote ? (
                        <Tooltip message="인용">
                            <ToolButton
                                type="button"
                                aria-label="인용"
                                $active={!!editor?.isActive("blockquote")}
                                disabled={disabled}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() =>
                                    runEditorCommand((nextEditor) =>
                                        nextEditor.chain().focus().toggleBlockquote().run(),
                                    )
                                }
                            >
                                <Icon name="quote" size="13" color="currentColor" />
                            </ToolButton>
                        </Tooltip>
                    ) : null}
                </>
            ) : null}
            {enabledFeatures.mention || enabledFeatures.emoji ? (
                <>
                    {enabledFeatures.formatting ||
                    enabledFeatures.link ||
                    enabledFeatures.list ||
                    enabledFeatures.quote ? (
                        <ToolbarDivider />
                    ) : null}
                    {enabledFeatures.mention ? (
                        <Tooltip message="멘션">
                            <ToolButton
                                type="button"
                                aria-label="멘션"
                                disabled={disabled}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={insertMentionTrigger}
                            >
                                @
                            </ToolButton>
                        </Tooltip>
                    ) : null}
                    {enabledFeatures.emoji ? (
                        <EmojiMenuAnchor>
                            <Tooltip message="이모지">
                                <ToolButton
                                    ref={emojiButtonRef}
                                    type="button"
                                    aria-label="이모지"
                                    $active={isEmojiMenuOpen}
                                    disabled={disabled}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => setIsEmojiMenuOpen((isOpen) => !isOpen)}
                                >
                                    <EmojiGlyph>☺</EmojiGlyph>
                                </ToolButton>
                            </Tooltip>
                            {isEmojiMenuOpen
                                ? createPortal(
                                      <EmojiMenu $left={emojiMenuPosition.left} $top={emojiMenuPosition.top}>
                                          {EMOJI_OPTIONS.map((emoji) => (
                                              <ToolButton
                                                  key={emoji}
                                                  type="button"
                                                  aria-label={`이모지 ${emoji}`}
                                                  onMouseDown={(event) => event.preventDefault()}
                                                  onClick={() => insertEmoji(emoji)}
                                              >
                                                  {emoji}
                                              </ToolButton>
                                          ))}
                                      </EmojiMenu>,
                                      document.body,
                                  )
                                : null}
                        </EmojiMenuAnchor>
                    ) : null}
                </>
            ) : null}
        </Toolbar>
    ) : null;

    return (
        <Container className={className} onClick={onClick} onMouseDown={onMouseDown} onMouseUp={onMouseUp}>
            <MessageInputGlobalStyle />
            {headerSlot ? <HeaderSlot>{headerSlot}</HeaderSlot> : null}
            {toolbar}
            <InputRow>
                {leftSlot ? <div>{leftSlot}</div> : null}
                <InputWrap>
                    <EditorWrap $compact={!multiline}>
                        <EditorContent editor={editor} />
                    </EditorWrap>
                </InputWrap>
                {renderedActions.length ? <>{renderedActions}</> : null}
            </InputRow>
            {footerSlot || bottomSlot ? (
                <SlotRow>
                    {footerSlot}
                    {bottomSlot}
                </SlotRow>
            ) : null}
        </Container>
    );
}

export default forwardRef(MessageInput);
